import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { DeclinedReason, DeclinedReasonService } from '@vendasta/sales-orders';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize, take } from 'rxjs/operators';

export enum Action {
  ADD = 'add',
  ARCHIVE = 'archive',
  EDIT = 'edit',
}

export interface DeclinedReasonDialogData {
  action: Action;
  partnerId?: string;
  reason?: DeclinedReason;
}

interface TranslatedData {
  pageTitle: string;
  saveButton: string;
  info?: string;
  inputLabel?: string;
  sucessMessage: string;
  errorMessage: string;
}

@Component({
  styleUrls: ['./declined-reason-dialog.component.scss'],
  templateUrl: './declined-reason-dialog.component.html',
  standalone: false,
})
export class DeclinedReasonDialogComponent implements OnInit {
  formGroup: UntypedFormGroup;
  translatedData: TranslatedData;
  saving$$ = new BehaviorSubject(false);
  action = Action;

  constructor(
    public dialogRef: MatDialogRef<DeclinedReasonDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DeclinedReasonDialogData,
    private fb: UntypedFormBuilder,
    private readonly declinedReasonsService: DeclinedReasonService,
    private translateService: TranslateService,
    private snackbarService: SnackbarService,
  ) {}

  ngOnInit(): void {
    this.formGroup = this.fb.group({
      reason: new UntypedFormControl(this.data.reason?.reason || null, [Validators.required]),
    });

    switch (this.data.action) {
      case Action.ADD:
        this.translatedData = {
          pageTitle: this.translateService.instant('CUSTOMIZE_SALES_ORDERS_DECLINE_REASONS.DIALOG.ADD_PAGE_TITLE'),
          saveButton: this.translateService.instant('CUSTOMIZE_SALES_ORDERS_DECLINE_REASONS.DIALOG.SAVE_BUTTON'),
          info: this.translateService.instant('CUSTOMIZE_SALES_ORDERS_DECLINE_REASONS.DIALOG.ADD_INFO'),
          inputLabel: this.translateService.instant('CUSTOMIZE_SALES_ORDERS_DECLINE_REASONS.DIALOG.INPUT_REASON_LABEL'),
          sucessMessage: this.translateService.instant('CUSTOMIZE_SALES_ORDERS_DECLINE_REASONS.DIALOG.ADD_SUCCESS'),
          errorMessage: this.translateService.instant('CUSTOMIZE_SALES_ORDERS_DECLINE_REASONS.DIALOG.ADD_ERROR'),
        };
        break;
      case Action.EDIT:
        this.translatedData = {
          pageTitle: this.translateService.instant('CUSTOMIZE_SALES_ORDERS_DECLINE_REASONS.DIALOG.EDIT_PAGE_TITLE'),
          saveButton: this.translateService.instant('CUSTOMIZE_SALES_ORDERS_DECLINE_REASONS.DIALOG.SAVE_BUTTON'),
          inputLabel: this.translateService.instant('CUSTOMIZE_SALES_ORDERS_DECLINE_REASONS.DIALOG.INPUT_REASON_LABEL'),
          sucessMessage: this.translateService.instant('CUSTOMIZE_SALES_ORDERS_DECLINE_REASONS.DIALOG.EDIT_SUCCESS'),
          errorMessage: this.translateService.instant('CUSTOMIZE_SALES_ORDERS_DECLINE_REASONS.DIALOG.EDIT_ERROR'),
        };
        break;
      case Action.ARCHIVE:
        this.translatedData = {
          pageTitle: this.translateService.instant('CUSTOMIZE_SALES_ORDERS_DECLINE_REASONS.DIALOG.ARCHIVE_PAGE_TITLE'),
          saveButton: this.translateService.instant('CUSTOMIZE_SALES_ORDERS_DECLINE_REASONS.DIALOG.ARCHIVE_BUTTON'),
          info: `${this.translateService.instant(
            'CUSTOMIZE_SALES_ORDERS_DECLINE_REASONS.DIALOG.ARCHIVE_INFO',
          )} <strong>${this.data.reason?.reason}</strong>`,
          sucessMessage: this.translateService.instant('CUSTOMIZE_SALES_ORDERS_DECLINE_REASONS.DIALOG.ARCHIVE_SUCCESS'),
          errorMessage: this.translateService.instant('CUSTOMIZE_SALES_ORDERS_DECLINE_REASONS.DIALOG.ARCHIVE_ERROR'),
        };
        break;
    }
  }

  save(): void {
    this.saving$$.next(true);

    let method: Observable<any>;
    switch (this.data.action) {
      case Action.ADD:
        method = this.declinedReasonsService.create(this.data.partnerId, {
          reason: this.formGroup.controls['reason'].value,
        });
        break;
      case Action.EDIT:
        method = this.declinedReasonsService.update(this.data.reason?.id, {
          reason: this.formGroup.controls['reason'].value,
        });
        break;
      case Action.ARCHIVE:
        method = this.declinedReasonsService.archive(this.data.reason?.id);
        break;
    }

    method
      .pipe(
        take(1),
        finalize(() => this.saving$$.next(false)),
      )
      .subscribe(
        () => {
          this.snackbarService.successSnack(this.translatedData.sucessMessage);
          this.dialogRef.close(true);
        },
        () => {
          this.snackbarService.errorSnack(this.translatedData.errorMessage);
        },
      );
  }

  displayInput(): boolean {
    return this.data.action === Action.ADD || this.data.action === Action.EDIT;
  }
}
